import React, { useState } from 'react';
import { GoogleLogin } from '@react-oauth/google';
import { Lock, Mail, Eye, EyeOff } from 'lucide-react';
import api from '../services/api';

const ChangePasswordModal = ({ onSuccess }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (newPassword.length < 8) {
      return setError('Password minimal 8 karakter');
    }
    if (newPassword !== confirmPassword) {
      return setError('Konfirmasi password tidak cocok');
    }

    setLoading(true);
    try {
      const result = await api.changePassword(newPassword);
      if (result === null) {
        setError('Sesi habis, silakan login ulang.');
        setLoading(false);
        return;
      }
      onSuccess();
    } catch (err) {
      setError(err.message || 'Gagal mengubah password');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999
    }}>
      <div style={{
        background: '#fff', borderRadius: '0.75rem', padding: '2rem',
        width: '100%', maxWidth: '420px', boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
      }}>
        <div style={{ textAlign: 'center', marginBottom: '1.5rem' }}>
          <div style={{
            width: '48px', height: '48px', borderRadius: '50%',
            background: '#E2EDEA', display: 'flex', alignItems: 'center',
            justifyContent: 'center', margin: '0 auto 0.75rem'
          }}>
            <Lock size={24} color="#1F5C57" />
          </div>
          <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700, color: '#1B2730' }}>
            Buat Password Baru
          </h2>
          <p style={{ margin: '0.5rem 0 0', fontSize: '0.875rem', color: '#55636B' }}>
            Anda login untuk pertama kali. Silakan buat password baru Anda.
          </p>
        </div>

        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Password Baru</label>
            <div className="input-wrapper" style={{ position: 'relative' }}>
              <Lock className="input-icon" size={20} />
              <input
                type={showNew ? 'text' : 'password'}
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                className="form-input"
                placeholder="Minimal 8 karakter"
                required
                disabled={loading}
                style={{ paddingRight: '2.5rem' }}
              />
              <button
                type="button"
                onClick={() => setShowNew(!showNew)}
                style={{
                  position: 'absolute', right: '0.75rem', top: '50%',
                  transform: 'translateY(-50%)', background: 'none',
                  border: 'none', cursor: 'pointer', padding: 0, color: '#7C8990'
                }}
              >
                {showNew ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div className="form-group">
            <label className="form-label">Konfirmasi Password</label>
            <div className="input-wrapper" style={{ position: 'relative' }}>
              <Lock className="input-icon" size={20} />
              <input
                type={showConfirm ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                className="form-input"
                placeholder="Ulangi password baru"
                required
                disabled={loading}
                style={{ paddingRight: '2.5rem' }}
              />
              <button
                type="button"
                onClick={() => setShowConfirm(!showConfirm)}
                style={{
                  position: 'absolute', right: '0.75rem', top: '50%',
                  transform: 'translateY(-50%)', background: 'none',
                  border: 'none', cursor: 'pointer', padding: 0, color: '#7C8990'
                }}
              >
                {showConfirm ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          {error && <div className="error-message">{error}</div>}

          <button type="submit" className="btn-login" disabled={loading}>
            {loading ? 'Menyimpan...' : 'Simpan Password'}
          </button>
        </form>
      </div>
    </div>
  );
};

const VALUES = [
  ['I', 'Integrity', 'Integritas dan kejujuran'],
  ['C', 'Compassion', 'Empati dan kepedulian'],
  ['A', 'Assurance', 'Jaminan kualitas'],
  ['R', 'Respect', 'Menghargai dan menghormati'],
  ['E', 'Embrace Innovation', 'Inovasi dan kreativitas'],
];

const LoginPage = ({ onLogin }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);

  const finalizeLogin = (userData) => {
    localStorage.setItem('user', JSON.stringify(userData));
    onLogin(userData);
  };

  const handleLoginResponse = (response) => {
    api.setTokens(response.accessToken, response.refreshToken);
    if (response.mustChangePassword) {
      setPendingUser(response.user);
    } else {
      finalizeLogin(response.user);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await api.login(email, password);
      if (response.success) {
        handleLoginResponse(response);
      }
    } catch (err) {
      setError(err.message || 'Login gagal');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setLoading(true);
    setError('');

    try {
      const response = await api.loginWithGoogle(credentialResponse.credential);
      if (response.success) {
        handleLoginResponse(response);
      }
    } catch (err) {
      setError(err.message || 'Login Google gagal');
    } finally {
      setLoading(false);
    }
  };

  const handleGoogleError = () => {
    setError('Login Google gagal. Silakan coba lagi.');
  };

  return (
    <>
      {pendingUser && (
        <ChangePasswordModal
          onSuccess={() => finalizeLogin(pendingUser)}
        />
      )}
      <div className="login-container">
        <aside className="login-values">
          <p className="login-values-school">
            <strong>STIK Sint Carolus</strong>
            Penilaian sikap mahasiswa berdasarkan nilai I-CARE
          </p>
          <ul className="value-list">
            {VALUES.map(([letter, name, desc]) => (
              <li key={letter} className="value-row">
                <span className="value-letter" aria-hidden="true">{letter}</span>
                <span className="value-name">{name}<span>{desc}</span></span>
              </li>
            ))}
          </ul>
        </aside>

        <main className="login-card">
          <div className="logo-container">
            <h1 className="login-title">Masuk ke I-CARE</h1>
            <p className="login-subtitle">Gunakan akun kampus Anda.</p>
          </div>

          {/* Google Login Button */}
          <div>
            <div style={{ display: 'flex', justifyContent: 'center' }}>
              <GoogleLogin
                onSuccess={handleGoogleSuccess}
                onError={handleGoogleError}
                useOneTap
                theme="outline"
                size="large"
                text="signin_with"
                shape="rectangular"
                width="100%"
              />
            </div>

            <div className="login-divider">atau dengan email</div>
          </div>

          <form onSubmit={handleLogin}>
            <div className="form-group">
              <label className="form-label">Email / Username</label>
              <div className="input-wrapper">
                <Mail className="input-icon" size={20} />
                <input
                  type="text"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="form-input"
                  placeholder="Masukkan email Anda"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <div className="input-wrapper">
                <Lock className="input-icon" size={20} />
                <input
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="form-input"
                  placeholder="••••••••••"
                  required
                  disabled={loading}
                />
              </div>
            </div>

            {error && <div className="error-message">{error}</div>}

            <button type="submit" className="btn-login" disabled={loading}>
              {loading ? 'Memproses...' : 'Masuk'}
            </button>
          </form>

          <p className="login-note">
            Belum bisa masuk? Hubungi admin prodi untuk mendaftarkan email Anda.
          </p>
        </main>
      </div>
    </>
  );
};

export default LoginPage;
